
import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';

const ContactPage: React.FC = () => {
  const [sent, setSent] = useState(false);

  return (
    <div className="pb-24">
      {/* Header */}
      <section className="bg-slate-50 py-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-black text-slate-900 mb-6">Let's Power Your Project</h1>
          <p className="text-slate-500 max-w-2xl mx-auto text-lg">
            Have a question about solar or subsidies? Reach out to our Bengaluru team.
          </p>
        </div>
      </section>

      <section className="py-24 container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Info Side */}
          <div className="lg:col-span-1 space-y-10">
            <div>
              <h3 className="text-2xl font-bold mb-8">Visit Our Office</h3>
              <div className="space-y-8">
                <div className="flex gap-6">
                  <div className="shrink-0 h-12 w-12 bg-green-50 text-green-700 rounded-xl flex items-center justify-center">
                    <MapPin />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 uppercase tracking-widest text-xs mb-1">Address</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">
                      No. 126/A, Ground Floor,<br />
                      Ullal Main Road, Vinayaka Layout,<br />
                      Bengaluru, Karnataka - 560056
                    </p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="shrink-0 h-12 w-12 bg-green-50 text-green-700 rounded-xl flex items-center justify-center">
                    <Phone />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 uppercase tracking-widest text-xs mb-1">Direct Lines</h4>
                    <p className="text-slate-500 text-sm">Sales: +91 98869 78555</p>
                    <p className="text-slate-500 text-sm">Tech: +91 63666 62555</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="shrink-0 h-12 w-12 bg-green-50 text-green-700 rounded-xl flex items-center justify-center">
                    <Mail />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 uppercase tracking-widest text-xs mb-1">Email</h4>
                    <p className="text-slate-500 text-sm">suryamitra.ceo@gmail.com</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="shrink-0 h-12 w-12 bg-green-50 text-green-700 rounded-xl flex items-center justify-center">
                    <Clock />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 uppercase tracking-widest text-xs mb-1">Business Hours</h4>
                    <p className="text-slate-500 text-sm">Mon – Sat: 9:00 AM - 7:00 PM</p>
                    <p className="text-slate-500 text-sm">Sun: Emergency Site Visits Only</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="pt-6">
              <a 
                href="https://wa.me/919886978555" 
                target="_blank" 
                className="flex items-center justify-center gap-3 bg-[#25D366] text-white p-6 rounded-3xl font-bold uppercase tracking-widest text-xs shadow-xl shadow-green-100 transition-all hover:scale-105"
              >
                <MessageCircle /> WhatsApp Our CEO Directly
              </a>
            </div>
          </div>

          {/* Form Side */}
          <div className="lg:col-span-2">
            <div className="bg-white p-10 md:p-16 rounded-[3rem] shadow-2xl border border-slate-50">
              {sent ? (
                <div className="text-center py-20 animate-in zoom-in duration-500">
                  <div className="h-24 w-24 bg-green-100 text-green-700 rounded-full flex items-center justify-center mx-auto mb-8">
                    <Send size={40} />
                  </div>
                  <h3 className="text-3xl font-black mb-4">Message Received!</h3>
                  <p className="text-slate-500 mb-8">Bettaswamy Gowda and the team will review your inquiry and contact you at suryamitra.ceo@gmail.com shortly.</p>
                  <button onClick={() => setSent(false)} className="text-green-700 font-bold uppercase tracking-widest text-xs underline">Send another inquiry</button>
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); setSent(true); }} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Full Name</label>
                      <input required type="text" className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 focus:ring-4 focus:ring-green-100 focus:border-green-600 outline-none transition-all" placeholder="Enter name" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Mobile Number</label>
                      <input required type="tel" className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 focus:ring-4 focus:ring-green-100 focus:border-green-600 outline-none transition-all" placeholder="+91 00000 00000" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Interest Area</label>
                    <select className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 focus:ring-4 focus:ring-green-100 focus:border-green-600 outline-none transition-all">
                      <option>Solar Water Pump (Horticulture)</option>
                      <option>Rooftop Solar (Residential On-Grid)</option>
                      <option>Off-Grid Solar Power System</option>
                      <option>Solar Street Lighting</option>
                      <option>Maintenance Request</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Message</label>
                    <textarea required className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-6 py-4 focus:ring-4 focus:ring-green-100 focus:border-green-600 outline-none transition-all min-h-[150px]" placeholder="Tell us about your project location and energy needs..."></textarea>
                  </div>
                  <button type="submit" className="w-full bg-green-700 hover:bg-green-800 text-white font-black uppercase tracking-widest py-6 rounded-2xl shadow-xl shadow-green-100 transition-all flex items-center justify-center gap-3">
                    Submit Inquiry <Send size={20} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="container mx-auto px-4">
        <div className="h-[400px] w-full bg-slate-100 rounded-[3rem] overflow-hidden relative shadow-inner">
           <img 
              src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?auto=format&fit=crop&q=80&w=2000" 
              className="absolute inset-0 w-full h-full object-cover opacity-20 grayscale" 
              alt="Map abstraction"
           />
           <div className="absolute inset-0 flex items-center justify-center text-slate-600 flex-col gap-4">
             <MapPin size={48} className="text-green-700" />
             <p className="font-bold uppercase tracking-widest text-xs">Ullal Main Road, Bengaluru Location</p>
           </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
